import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { PeriodSelector } from '../PeriodSelector/PeriodSelector';
import type { PeriodValue } from '../../lib/period';
import { INV_NUMBERS, invHoldStore } from '../../invisalign/mockData';
import type { InvStage } from '../../invisalign/types';
import { INV_STAGE_META } from '../../invisalign/mockData';

export type InvTabId = 'overview' | 'prospects' | 'line';

const TABS: { id: InvTabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'prospects', label: 'Prospects' },
  { id: 'line', label: 'Invis-Line' },
];

export interface InvShellProps {
  active: InvTabId;
  onSelectTab?: ((tab: InvTabId) => void) | undefined;
  onOpenSettings?: (() => void) | undefined;
  onHold: boolean;
  onSetHold?: ((next: boolean) => void) | undefined;
  /** The report period (spec 41) — the selector shows on the Overview tab only. */
  period?: PeriodValue | undefined;
  onPeriodChange?: ((value: PeriodValue) => void) | undefined;
  children: React.ReactNode;
}

/**
 * The Invisalign Coordinator header + four tabs (amendment 30 §3), shared by
 * Overview, Prospects and Invis-Line. The sub says the provider level;
 * Hold uses the shared hold modal, then a held banner + Resume (as TC).
 */
export function InvShell({ active, onSelectTab, onOpenSettings, onHold, onSetHold, period, onPeriodChange, children }: InvShellProps) {
  const [asking, setAsking] = useState(false);
  const n = INV_NUMBERS;
  const sub = n.isProvider
    ? `Email, texts and the Invis-Line · Alamo Heights Dental (San Antonio, TX) · ${n.providerLevel} Invisalign provider`
    : 'Email, texts and the Invis-Line · Alamo Heights Dental (San Antonio, TX) · clear aligners';

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1084px] flex-1 overflow-y-auto px-[30px] pb-16 pt-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="pc-eyebrow text-[var(--text-secondary)]">{n.ready} ready for a nudge · {onHold ? 'on hold' : 'working'}</div>
          <h1 className="m-0 mt-0.5 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
            Invisalign <span className="text-[var(--brand-indigo)]">Coordinator</span>
          </h1>
          <p className="m-0 mt-0.5 text-[12.5px] text-[var(--text-secondary)]">{sub}</p>
        </div>
        <div className="flex items-center gap-2">
          {active === 'overview' && period && onPeriodChange ? (
            <PeriodSelector variant="agent" value={period} onChange={onPeriodChange} startedISO="2026-03-03" />
          ) : null}
          <Button variant="secondary" size="sm" iconLeft="cog" onClick={onOpenSettings}>Settings</Button>
          {onHold ? (
            <Button variant="primary" size="sm" iconLeft="play" onClick={() => { onSetHold?.(false); }}>Resume</Button>
          ) : (
            <Button variant="danger" size="sm" iconLeft="pause" onClick={() => { setAsking(true); }}>Hold</Button>
          )}
        </div>
      </div>

      {onHold ? (
        <section className="mt-4 flex flex-wrap items-center gap-3 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--warn-amber)_35%,transparent)] bg-[var(--warn-amber-fill)] px-4 py-2.5">
          <Icon kind="pause" size={15} stroke={2} style={{ color: 'var(--warn-amber)' }} />
          <span className="text-[13px] font-semibold text-[var(--warn-amber)]">Invisalign Coordinator is on hold</span>
          <span className="text-[12px] text-[var(--text-secondary)]">No emails or texts go out until you resume. The Invis-Line still answers.</span>
        </section>
      ) : null}

      <div className="mt-4 flex items-center gap-1 border-b border-[var(--border-default)]">
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => { onSelectTab?.(t.id); }}
            className={cn('-mb-px border-b-2 px-3.5 pb-2.5 pt-1 text-[14px]', t.id === active ? 'border-[var(--brand-indigo)] font-bold text-[var(--text-primary)]' : 'border-transparent font-medium text-[var(--text-muted)] hover:text-[var(--text-secondary)]')}>
            {t.label}
          </button>
        ))}
      </div>

      {children}

      {asking ? (
        <HoldModal onCancel={() => { setAsking(false); }} onConfirm={() => { setAsking(false); onSetHold?.(true); }} />
      ) : null}
    </div>
  );
}

/** The shared stage chip (§1) — same word, same colour on every page. */
export function InvStageChip({ stage }: { stage: InvStage }) {
  const m = INV_STAGE_META[stage];
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ color: m.fg, background: m.bg }}>
      {m.label}
    </span>
  );
}

function HoldModal({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  void invHoldStore;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(20,22,28,0.35)] px-4" role="dialog" aria-modal="true" aria-labelledby="hold-inv-title">
      <div className="w-full max-w-[420px] rounded-[var(--r-lg)] border border-[var(--border-default)] bg-[var(--surface-card)] p-5 shadow-[var(--shadow-modal)]">
        <h2 id="hold-inv-title" className="m-0 text-[16px] font-bold text-[var(--text-primary)]">Hold Invisalign Coordinator?</h2>
        <p className="m-0 mt-2 text-[13px] leading-[1.6] text-[var(--text-secondary)]">No emails or texts go out until you resume. The Invis-Line still answers, and the ready patients stay in Calls.</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={onConfirm}>Hold</Button>
        </div>
      </div>
    </div>
  );
}
